import { Box, Grid, Typography } from '@mui/material';
import FormikAutosuggest from 'components/formik/Autosuggest';
import Language from 'components/formik/Language';
import TextField from 'components/formik/TextField';
import TimeZone from 'components/formik/TimeZone';
import { Field, useFormikContext } from 'formik';
import { arrayOf, string } from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import { I18n } from 'utils/i18n';

const fieldSx = { padding: '5px' };

const labelSx = {
  display: 'flex',
  alignItems: 'center',
};

const JobDetails = ({ divisions = [] }) => {
  const { values, errors, touched } = useFormikContext();
  const codeInputRef = useRef();

  const validateCode = useCallback(
    value => {
      if (!value) {
        return undefined;
      }

      const pattern = {
        number: /^[0-9]*$/,
        alphanumeric: /^[a-zA-Z0-9]*$/,
        spanishSSN: /^(\d{2})\/(\d{8})(-(\d{2}))?$/,
      }[values.codeType];

      if (pattern && !pattern.test(value)) {
        return I18n.t(`ValidationErrors.${values.codeType}`, {
          field: I18n.t('EmployeeDrawer.Code'),
          value,
        });
      }

      return undefined;
    },
    [values.codeType],
  );

  useEffect(() => {
    const { current } = codeInputRef;
    if (!current) return () => {};

    const handleInput = () => {
      const errorMessage = validateCode(current.value);
      current.setCustomValidity(errorMessage || '');
    };

    current.addEventListener('input', handleInput);
    return () => {
      current.removeEventListener('input', handleInput);
    };
  }, [validateCode]);

  return (
    <Box sx={{ pt: '20px' }}>
      <Typography variant="caption">{I18n.t('EmployeeDrawer.JOB DETAILS')}</Typography>
      <Grid container>
        {divisions.length > 0 && (
          <>
            <Grid sx={labelSx} size={2}>
              <Typography>{`${I18n.t('EmployeeDrawer.Division')}*`}</Typography>
            </Grid>
            <Grid size={10}>
              <Box sx={fieldSx}>
                <Field
                  component={FormikAutosuggest}
                  suggestions={divisions.map(s => ({ label: s, value: s }))}
                  placeholder={I18n.t('EmployeeDrawer.Start typing')}
                  name="division"
                />
              </Box>
            </Grid>
          </>
        )}
        <Grid sx={labelSx} size={2}>
          <Typography>{`${I18n.t('EmployeeDrawer.Code')}`}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="code"
            validate={validateCode}
            error={touched.code && errors.code}
            helperText={touched.code && errors.code}
            inputRef={codeInputRef}
            slotProps={{
              htmlInput: {
                inputMode: values.codeType === 'number' ? 'numeric' : 'text',
                pattern: {
                  number: '^[0-9]*$',
                  alphanumeric: '^[a-zA-Z0-9]*$',
                  spanishSSN: '^(\\d{2})\\/(\\d{8})(-(\\d{2}))?$',
                }[values.codeType],
              },
            }}
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{`${I18n.t('EmployeeDrawer.Id')}`}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="id"
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{`${I18n.t('EmployeeDrawer.Job title')}*`}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="jobTitle"
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{`${I18n.t('EmployeeDrawer.Department')}*`}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="department"
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{I18n.t('EmployeeDrawer.Location')}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="location"
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{I18n.t('EmployeeDrawer.Report to')}</Typography>
        </Grid>
        <Grid size={10}>
          <Field
            component={TextField}
            sx={fieldSx}
            placeholder={I18n.t('EmployeeDrawer.Start typing')}
            name="reportsTo"
          />
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{I18n.t('EmployeeDrawer.Language')}</Typography>
        </Grid>
        <Grid size={10}>
          <Box sx={fieldSx}>
            <Field component={Language} name="language" />
          </Box>
        </Grid>
        <Grid sx={labelSx} size={2}>
          <Typography>{I18n.t('EmployeeDrawer.Time zone')}</Typography>
        </Grid>
        <Grid size={10}>
          <Box sx={fieldSx}>
            <Field component={TimeZone} name="timeZone" />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

JobDetails.propTypes = {
  divisions: arrayOf(string),
};

export default JobDetails;
